/* oxlint-disable jsx-a11y/prefer-tag-over-role */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';

import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import style from './todo-list-style.module.less';
import { TODO_REMOVED, TODO_NAV_CLICKED, TODO_MARKED_COMPLETE } from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';
import { useFTUWizardContext } from '../ftu-wizard/context/ftu-wizard-context';

/**
 * TodoListItem component for displaying a single todo item in a list
 * @param {object} props - Component props
 * @param {object} props.title - Title message descriptor for FormattedMessage
 * @param {object} props.description - Description message descriptor for FormattedMessage
 * @param {object} props.linkText - Link text message descriptor for FormattedMessage
 * @param {string} props.id - Unique identifier for the item
 * @param {string} props.link - URL for the action link
 * @returns {React.ReactElement} The TodoListItem component
 */
export const TodoListItem = ({ description, id, link, linkText, onLinkClick, title }) => {
  const navigate = useNavigate();
  const { todosInLocalState = [], updateTodosWithSave } = useFTUWizardContext();
  const currentTodo = todosInLocalState.find((todo) => todo.id === id);
  const [isCompleted, setIsCompleted] = useState(currentTodo?.isCompleted ?? false);

  const handleCheckboxChange = () => {
    const newCompletedState = !isCompleted;

    Segment.trackEvent(TODO_MARKED_COMPLETE, {
      isCompleted: newCompletedState,
      todoId: id,
    });

    setIsCompleted(newCompletedState);

    let updatedTodos;
    if (currentTodo) {
      updatedTodos = todosInLocalState.map((todo) => {
        if (todo.id === currentTodo.id) {
          return {
            ...todo,
            isCompleted: newCompletedState,
          };
        }
        return todo;
      });
    } else {
      // Add new todo with default values
      updatedTodos = [
        ...todosInLocalState,
        {
          id,
          isVisible: false,
          isCompleted: newCompletedState,
        },
      ];
    }
    updateTodosWithSave(updatedTodos);
  };

  const handleRemoveClick = useCallback(() => {
    Segment.trackEvent(TODO_REMOVED, {
      todoId: id,
    });

    const existingTodoIndex = todosInLocalState.findIndex((todo) => todo.id === id);
    let updatedTodos;

    if (existingTodoIndex !== -1) {
      updatedTodos = todosInLocalState.map((todo) => {
        if (todo.id === id) {
          return {
            ...todo,
            isVisible: false,
            isCompleted: false,
          };
        }
        return todo;
      });
    } else {
      // Add new todo as hidden (similar to complete logic)
      updatedTodos = [
        ...todosInLocalState,
        {
          id,
          isVisible: false,
          isCompleted: false,
        },
      ];
    }
    updateTodosWithSave(updatedTodos);
  }, [id, updateTodosWithSave, todosInLocalState]);

  const onNavigationClick = useCallback(() => {
    Segment.trackEvent(TODO_NAV_CLICKED, {
      todoId: id,
    });

    // Check if there's a custom click handler
    if (onLinkClick) {
      const handled = onLinkClick(id);
      if (handled) {
        return;
      }
    }

    const isExternalLink = link?.startsWith('http://') || link?.startsWith('https://');

    if (isExternalLink) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link);
    }
  }, [id, link, navigate, onLinkClick]);

  return (
    <FlexColumn
      className={classNames(style['todo-list-item'], {
        [style['marked-complete']]: isCompleted,
      })}
    >
      <FlexRow className={style['item-header']}>
        <div className={style['check-button']}>
          {/* oxlint-disable-next-line jsx-a11y/interactive-supports-focus */}
          <Button
            aria-checked={isCompleted}
            className={classNames(style.checkbox, {
              [style['check-button-checked']]: isCompleted,
            })}
            id={`todo-checkbox-${id}`}
            onClick={handleCheckboxChange}
            role='checkbox'
            theme='unstyled'
          >
            {isCompleted ? <FontAwesomeIcon className={style['check-icon']} icon={faCheck} /> : null}
          </Button>
        </div>
        <Text className={style['item-title']} textType={TextType.H4} value={title} />
        <Button className={style.link} onClick={onNavigationClick} theme='link'>
          <FormattedMessage {...linkText} />
        </Button>
        <div className={style.separator} />
        <Button className={style['close-button']} onClick={handleRemoveClick} theme='unstyled'>
          <FontAwesomeIcon icon={faXmark} />
        </Button>
      </FlexRow>
      {description ? <Text className={style['item-description']} textType={TextType.Paragraph} value={description} /> : null}
    </FlexColumn>
  );
};
